import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '../../hooks';
import t from '../../locales/en/main.json';
import { useLazyFetchInspectionsItemQuery } from '../../services/modules/inspection';
import { setCurrentCar } from '../../store/inspection';

import { IFooter } from './Footer';
import {
  Container,
  ProgressBar,
  ProgressContainer,
  RowContent,
  Timer,
  Title,
} from './FooterElements';

const TIME_FOR_INSPECTION = 30;

const Footer: React.FC<IFooter.IProps> = (props) => {
  const { slug, isForceGetDetail, children } = props;
  const dispatch = useDispatch();
  const Ref = useRef<any>(null);
  const [remainTime, setRemainTime] = useState(0);
  const [progressValue, setProgressValue] = useState(0);
  const [startTime, setStartTime] = useState('');

  const { currentCar } = useAppSelector((state) => state.inspection);

  const [fetchInspectionsItem, { data, isSuccess }] = useLazyFetchInspectionsItemQuery();

  useEffect(() => {
    if ((slug && !currentCar) || (slug && isForceGetDetail)) {
      fetchInspectionsItem(slug);
    } else if (currentCar?.inspection_start_at) {
      setStartTime(currentCar.inspection_start_at);
    }
  }, []);

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(setCurrentCar(data));
      if (data.inspection_start_at) {
        setStartTime(data.inspection_start_at);
      }
    }
  }, [data, isSuccess]);

  const getSameTimeZone = (date: Date) => {
    return Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    );
  };

  const handleProgress = (second: number) => {
    const progress = (second / 60 / TIME_FOR_INSPECTION) * 100;
    setProgressValue(progress);
  };

  const handleTimer = useCallback(() => {
    let startDate = new Date(startTime);
    startDate.setMinutes(startDate.getMinutes() + TIME_FOR_INSPECTION);
    const finishTime = getSameTimeZone(startDate);
    const now = getSameTimeZone(new Date());
    if (isNaN(finishTime) || now > finishTime) {
      setRemainTime(0);
      setProgressValue(0);
    } else {
      const second = Math.floor((finishTime - now) / 1000);
      handleProgress(second);
      setRemainTime(second);
    }
  }, [startTime]);

  useEffect(() => {
    if (!remainTime) return;
    Ref.current = setInterval(() => {
      setRemainTime(remainTime - 1);
      handleProgress(remainTime);
    }, 1000);
    return () => clearInterval(Ref.current);
  }, [remainTime]);

  useEffect(() => {
    handleTimer();
  }, [startTime]);

  const formattedTime = (seconds: number) => {
    return new Date(seconds * 1000).toISOString().slice(14, 19);
  };

  return (
    <Container>
      <RowContent>
        {!!startTime && (
          <>
            <Title>{t.footer.remaining}:</Title>
            <Timer>
              <span className="icon-clock" />
              {formattedTime(remainTime)}
            </Timer>
          </>
        )}
        {!!startTime && (
          <ProgressContainer>
            <ProgressBar style={{ width: `${progressValue}%` }} />
          </ProgressContainer>
        )}
      </RowContent>
      {children}
    </Container>
  );
};

export { Footer };
